import { selectNextActiveTask } from './selectNextActiveTask';
import {
  AppointmentTask,
  RetryConfig,
  TaskProcessorDependencies,
  TaskProcessorInput,
  TaskProcessorInterface,
} from './types';

const DEFAULT_FOREGROUND_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 5,
  initialDelayMs: 1000, // 1 second
  maxDelayMs: 30000, // 30 seconds
  backoffMultiplier: 2,
};

const DEFAULT_BACKGROUND_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 10,
  initialDelayMs: 250, // 250 milliseconds
  maxDelayMs: 5000, // 5 seconds
  backoffMultiplier: 2,
};

/**
 * Processes appointment tasks serially with exponential backoff retry strategies.
 * Foreground tasks have faster, shorter retry cycles while background tasks retry
 * more slowly over a longer period.
 */
export class TaskProcessor implements TaskProcessorInterface {
  private userId: string;
  private tenantId: string;
  private _tasks: AppointmentTask[] = [];
  private isProcessing = false;
  public isInitialized = false;
  private isPaused = false;
  private dependencies: TaskProcessorDependencies;
  private foregroundRetryConfig: Required<RetryConfig>;
  private backgroundRetryConfig: Required<RetryConfig>;
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor({ userId, tenantId, dependencies }: TaskProcessorInput) {
    this.userId = userId;
    this.tenantId = tenantId;
    this.dependencies = dependencies;

    this.foregroundRetryConfig = {
      ...DEFAULT_FOREGROUND_RETRY_CONFIG,
      ...dependencies.retryConfig?.foreground,
    };

    this.backgroundRetryConfig = {
      ...DEFAULT_BACKGROUND_RETRY_CONFIG,
      ...dependencies.retryConfig?.background,
    };
  }

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      this._tasks = this.dependencies.getPersistedTasks(
        this.tenantId,
        this.userId,
      );
      this.isInitialized = true;

      // Only process queue if there are persisted tasks to process
      // This prevents double-processing when addTask() calls initialize()
      if (this._tasks.length > 0) {
        await this.processTaskQueue();
      }
    } catch (error) {
      this.dependencies.logger.error('Failed to initialize TaskProcessor', {
        error,
      });
      throw error;
    }
  }

  async addTask(task: AppointmentTask) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const taskKey = this.getTaskDeduplicationKey(task);
    const existingTask = this._tasks.find(
      (t) => this.getTaskDeduplicationKey(t) === taskKey,
    );

    if (existingTask) {
      // If existing task has a retry scheduled, clear it to process immediately
      if (existingTask.retry?.nextRetryAt) {
        this.dependencies.logger.info(
          'Task already exists, resetting retry time',
          {
            taskType: task.type,
            appointmentId: task.appointmentId,
          },
        );

        existingTask.retry.nextRetryAt = Date.now();
        this.persistTasks();

        this.triggerQueueProcessing();
      } else {
        this.dependencies.logger.info('Task already in queue, skipping add', {
          taskType: task.type,
          appointmentId: task.appointmentId,
        });
      }
      return;
    }

    // Initialize retry metadata if not present
    if (!task.retry) {
      task.retry = {
        retryCount: 0,
      };
    }

    // If task has existing retry metadata (retryCount > 0 and lastAttemptAt),
    // calculate nextRetryAt based on the backoff schedule
    if (
      task.retry.retryCount > 0 &&
      task.retry.lastAttemptAt &&
      !task.retry.nextRetryAt
    ) {
      task.retry.nextRetryAt = this.calculateNextRetryTime(task);

      this.dependencies.logger.info(
        'Task added with existing retry metadata, calculated nextRetryAt',
        {
          taskType: task.type,
          appointmentId: task.appointmentId,
          retryCount: task.retry.retryCount,
          lastAttemptAt: new Date(task.retry.lastAttemptAt).toISOString(),
          nextRetryAt: new Date(task.retry.nextRetryAt).toISOString(),
        },
      );
    }

    this._tasks.push(task);
    this.persistTasks();

    this.triggerQueueProcessing();
  }

  private async processTaskQueue() {
    if (this.isProcessing || this.isPaused) {
      return;
    }

    this.isProcessing = true;

    try {
      while (this._tasks.length > 0) {
        // Check if paused before processing next task
        if (this.isPaused) {
          break;
        }

        const nextTask = selectNextActiveTask(this._tasks);

        if (!nextTask) {
          break;
        }

        const success = await this.processTask(nextTask);

        if (success) {
          // Remove successful tasks
          this._tasks = this._tasks.filter((task) => task !== nextTask);
        }
        this.persistTasks();
      }
    } catch (error) {
      this.dependencies.logger.error('Error processing task queue', { error });
    } finally {
      this.isProcessing = false;

      // Schedule next processing attempt if there are pending tasks
      if (this._tasks.length > 0) {
        this.scheduleNextProcessing();
      }
    }
  }

  private isBackgroundTask(task: AppointmentTask): boolean {
    return Boolean(task.isBackgroundTask);
  }

  private getRetryConfigForTask(task: AppointmentTask): Required<RetryConfig> {
    const isBackground = this.isBackgroundTask(task);
    const baseRetryConfig = isBackground
      ? this.backgroundRetryConfig
      : this.foregroundRetryConfig;
    const taskSpecificConfig = isBackground
      ? task.retryConfig?.background
      : task.retryConfig?.foreground;

    return {
      ...baseRetryConfig,
      ...taskSpecificConfig,
    };
  }

  private calculateNextRetryTime(task: AppointmentTask): number {
    const retryConfig = this.getRetryConfigForTask(task);

    const retryCount = task.retry?.retryCount ?? 0;
    const delay = Math.min(
      retryConfig.initialDelayMs *
        Math.pow(retryConfig.backoffMultiplier, Math.max(0, retryCount - 1)),
      retryConfig.maxDelayMs,
    );

    const baseTime = task.retry?.lastAttemptAt || Date.now();
    return baseTime + delay;
  }

  private scheduleNextProcessing() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }

    const now = Date.now();
    const nextRetryTask = this._tasks
      .filter((task) => task.retry?.nextRetryAt)
      .sort(
        (a, b) => (a.retry?.nextRetryAt ?? 0) - (b.retry?.nextRetryAt ?? 0),
      )[0];

    const delay = nextRetryTask?.retry?.nextRetryAt
      ? Math.max(0, nextRetryTask.retry.nextRetryAt - now)
      : 100; // Fallback for tasks without retry schedule

    this.retryTimeoutId = setTimeout(() => this.processTaskQueue(), delay);
  }

  private async processTask(task: AppointmentTask): Promise<boolean> {
    try {
      const handler = this.dependencies.taskHandlers[task.type];

      if (!handler) {
        this.dependencies.logger.warn('Unknown task type', {
          task,
        });
        return true; // Remove unknown task types
      }

      await handler(task, {
        userId: this.userId,
        tenantId: this.tenantId,
      });

      return true; // Success
    } catch (error) {
      this.dependencies.logger.warn('Task processing failed', {
        error,
        taskType: task.type,
        appointmentId: task.appointmentId,
        retryCount: task.retry?.retryCount ?? 0,
      });

      return this.handleTaskFailure(task);
    }
  }

  private handleTaskFailure(task: AppointmentTask): boolean {
    const retryConfig = this.getRetryConfigForTask(task);

    const retryCount = (task.retry?.retryCount ?? 0) + 1;

    if (retryCount > retryConfig.maxRetries) {
      this.dependencies.logger.error('Task exceeded max retries, giving up', {
        taskType: task.type,
        appointmentId: task.appointmentId,
        retryCount,
        maxRetries: retryConfig.maxRetries,
      });
      return true; // Remove task that exceeded max retries
    }

    const now = Date.now();

    // Update task retry metadata
    task.retry = {
      retryCount,
      lastAttemptAt: now,
    };

    // Calculate nextRetryAt using the same logic as addTask
    task.retry.nextRetryAt = this.calculateNextRetryTime(task);

    const delay = task.retry.nextRetryAt - now;

    this.dependencies.logger.info('Task scheduled for retry', {
      taskType: task.type,
      appointmentId: task.appointmentId,
      retryCount,
      delayMs: delay,
      nextRetryAt: task.retry.nextRetryAt
        ? new Date(task.retry.nextRetryAt).toISOString()
        : undefined,
    });

    return false; // Keep task in queue for retry
  }

  private persistTasks() {
    try {
      this.dependencies.persistAppointmentTasks(
        this.tenantId,
        this.userId,
        this._tasks,
      );
    } catch (error) {
      this.dependencies.logger.error('Failed to persist tasks', { error });
    }
  }

  private triggerQueueProcessing() {
    if (!this.isProcessing) {
      setTimeout(() => this.processTaskQueue(), 0);
    }
  }

  private getTaskDeduplicationKey(task: AppointmentTask): string {
    const base = `${task.type}:${task.appointmentId}`;

    switch (task.type) {
      case 'TASK.GENERATE_NOTE':
        return `${base}`;
      case 'TASK.UPDATE_SEGMENT_RECORDING_ENDED_AT':
        return `${base}:${task.segmentId}`;
      case 'TASK.MARK_CLIENT_AUDIO_COMPLETED_AT':
        return `${base}:${task.fileId}`;
      case 'TASK.ADD_APPOINTMENT_TAG': {
        const baseWithOptionalFileId = `${base}${task.fileId ? `:${task.fileId}` : ''}`;
        return `${baseWithOptionalFileId}:${task.tag.type}:${task.tag.key}`;
      }
      default:
        return `${base}:${task.fileName}`;
    }
  }

  hasTasksForAppointment(appointmentId: string): boolean {
    return this._tasks.some((task) => task.appointmentId === appointmentId);
  }

  pause() {
    this.dependencies.logger.debug('Pausing task processor');

    this.isPaused = true;

    // Clear any scheduled retries
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
  }

  resume() {
    if (!this.isInitialized || !this.isPaused) return;

    this.dependencies.logger.debug('Resuming task processor');

    this.isPaused = false;

    this.resetTasksFromStorage();

    // Clear any old retry times that accumulated while paused
    const now = Date.now();
    this._tasks.forEach((task) => {
      if (task.retry?.nextRetryAt && task.retry.nextRetryAt < now) {
        // Reset past retry times to process immediately
        task.retry.nextRetryAt = now;
      }
    });

    // Start processing immediately if tasks exist
    if (this._tasks.length > 0) {
      this.triggerQueueProcessing();
    }
  }

  resetTasksFromStorage() {
    this._tasks = this.dependencies.getPersistedTasks(
      this.tenantId,
      this.userId,
    );
  }

  getTaskCount(): number {
    return this._tasks.length;
  }

  getTasks() {
    return [...this._tasks];
  }

  isProcessingTasks(): boolean {
    return this.isProcessing;
  }

  getTenantId(): string {
    return this.tenantId;
  }

  getUserId(): string {
    return this.userId;
  }

  cleanup() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
  }
}
